/**
 * Phase 19 — Promoted from /home-v2 to the canonical / homepage.
 * Editorial layout adapted from the design artifact. Reuses shared sections
 * below the fold from `src/components/home/sections.jsx` (single source of truth).
 *
 * Brand overrides from the artifact:
 *   • #E8412E in artifact  → #FF4343 (fc-red) here
 *   • #0E1438 in artifact  → #060A24 (dark navy card) here
 *   • #FAFAF8 in artifact  → #F0F1F5 (light gray) here
 *   • DM Serif Display     → Montserrat black (fc-h-hero / fc-h-section)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import useScrollReveal from "../hooks/useScrollReveal";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { SponsorBanner } from "../components/PublicBanners";
import {
  UpcomingEventsSection,
  AudienceCardsSection,
  FeaturedCoursesSection,
  TestimonialsSection,
  PricingTeaserSection,
} from "../components/home/sections";

const STEPS_V2 = [
  { title: "Choose your niche", body: "Pick your audience track — Automotive, Business, or Entrepreneur." },
  { title: "Learn from humans", body: "Live weekly sessions plus curated on-demand lessons." },
  { title: "Build real projects", body: "Walk away with deployable AI workflows for your team." },
  { title: "Earn certificates", body: "Prove what you've learned with verifiable credentials." },
];

const ESSENTIALS_BULLETS = [
  "Get a clear map of the AI landscape and where value actually lives.",
  "See the tools top teams use, and when to use which.",
  "Walk away with deployable AI workflows for your business.",
  "Figure out where AI fits your team, product, and customers.",
  "Connect with a community of operators building the same way.",
];

const ESSENTIALS_ROWS = [
  { label: "Format", value: "4-week live cohorts + on-demand LMS courses" },
  { label: "Access", value: "Community + weekly office hours" },
  { label: "Investment", value: "Complimentary (early access)" },
  { label: "Certificate", value: "Yes, verifiable" },
];

export default function Home() {
  const ref = useScrollReveal();
  const [courses, setCourses] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/courses").then(r => r.data).catch((e) => { console.error(e); return []; }),
      api.get("/plans").then(r => r.data).catch((e) => { console.error(e); return []; }),
    ]).then(([c, p]) => { setCourses(c); setPlans(p); });
  }, []);

  const featured = courses
    .filter(c => c.is_featured)
    .slice()
    .sort((a, b) => Number(!!a.is_coming_soon) - Number(!!b.is_coming_soon))
    .slice(0, 6);

  return (
    <div ref={ref} className="min-h-screen text-white" data-testid="home-v2-page">
      <Navbar promoLocation="homepage" />
      <SponsorBanner location="homepage" />

      {/* ── 1. HERO — dark navy, centered, radial red glow ─────────────── */}
      <section
        className="relative bg-fc-navy pt-32 pb-24 lg:pt-40 lg:pb-28 text-center overflow-hidden"
        data-testid="home-v2-hero"
      >
        {/* Grid pattern */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.024) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.024) 1px, transparent 1px)",
            backgroundSize: "76px 76px",
          }}
        />
        {/* Radial red glow */}
        <div
          className="absolute top-[-80px] left-1/2 -translate-x-1/2 w-[900px] h-[480px] pointer-events-none"
          style={{
            background:
              "radial-gradient(rgba(255,67,67,0.14) 0%, transparent 68%)",
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 reveal">
          <span
            className="inline-flex items-center bg-fc-red/10 border border-fc-red/25 text-fc-red/90 text-[11px] font-bold uppercase tracking-[0.14em] px-4 py-2 rounded-full mb-8"
            data-testid="home-v2-hero-pill"
          >
            Freddy Compute Academy
          </span>
          <h1
            className="font-black text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] tracking-tight mb-8"
            data-testid="home-v2-hero-headline"
          >
            Where businesses come to <span className="text-fc-red">learn AI.</span>
          </h1>
          <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-xl mx-auto mb-11">
            Build real AI capability across your organization today.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 bg-fc-red hover:bg-fc-red/90 text-white font-bold text-sm px-8 py-4 rounded-full transition-all"
              data-testid="home-v2-hero-cta-primary"
            >
              Explore the Curriculum <ArrowRight size={16} />
            </Link>
            <Link
              to="/request-info"
              className="inline-flex items-center gap-2 border border-white/20 text-white/75 hover:border-white hover:text-white font-bold text-sm px-8 py-4 rounded-full transition-all"
              data-testid="home-v2-hero-cta-secondary"
            >
              Request info
            </Link>
          </div>
        </div>
      </section>

      {/* ── 2. HOW IT WORKS — dark navy, 4 numbered dark cards ─────────── */}
      <section
        className="bg-fc-navy py-24 border-t border-white/[0.07]"
        data-testid="home-v2-how-it-works"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-14 reveal">
            <div className="text-fc-red text-[11px] font-bold uppercase tracking-[0.14em] mb-4">
              How it works
            </div>
            <h2 className="font-black text-white text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.05] tracking-tight max-w-4xl">
              From watching to <span className="text-fc-red">building</span> in four steps.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 reveal">
            {STEPS_V2.map((s, i) => (
              <div
                key={i}
                className="bg-[#060A24] border border-white/[0.08] rounded-xl p-8 hover:border-fc-red/40 transition-colors"
                data-testid={`home-v2-step-${i + 1}`}
              >
                <div className="w-10 h-10 rounded-full bg-fc-red flex items-center justify-center font-black text-white text-lg mb-6">
                  {i + 1}
                </div>
                <h3 className="font-black text-white text-[13px] uppercase tracking-[0.08em] mb-3">
                  {s.title}
                </h3>
                <p className="text-white/50 text-[13px] leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. AI ESSENTIALS — light bg, 2-col split ───────────────────── */}
      <section
        className="bg-[#F0F1F5] py-24"
        data-testid="home-v2-ai-essentials"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-fc-red text-[11px] font-bold uppercase tracking-[0.14em] mb-4 reveal">
            AI essentials
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12 lg:gap-20 items-start reveal">
            {/* Left column — headline + bullets */}
            <div>
              <h2 className="font-black text-fc-navy text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.05] tracking-tight mb-10">
                Key <span className="text-fc-red">Concepts</span>
              </h2>
              <ul className="flex flex-col gap-4 text-fc-navy text-lg leading-[1.7]">
                {ESSENTIALS_BULLETS.map((b, i) => (
                  <li
                    key={i}
                    className="flex gap-3 items-start"
                    data-testid={`home-v2-essentials-bullet-${i + 1}`}
                  >
                    <span className="text-fc-red flex-shrink-0 mt-[6px] text-lg font-bold">→</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right column — summary card */}
            <aside
              className="bg-white border border-fc-navy/10 rounded-xl shadow-[0_2px_12px_rgba(0,0,0,0.05)] overflow-hidden"
              data-testid="home-v2-essentials-card"
            >
              <div className="divide-y divide-fc-navy/10">
                {ESSENTIALS_ROWS.map((r) => (
                  <div key={r.label} className="px-7 py-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.09em] text-fc-navy/45 mb-1">
                      {r.label}
                    </div>
                    <div className="text-[15px] font-semibold text-fc-navy leading-tight">
                      {r.value}
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/request-info"
                className="flex items-center justify-between bg-fc-red hover:bg-fc-red/90 text-white text-sm font-bold px-7 py-4 transition-colors"
                data-testid="home-v2-essentials-cta"
              >
                Request info <ArrowRight size={16} />
              </Link>
            </aside>
          </div>
        </div>
      </section>

      {/* ── Below the 3 new sections — reuse shared components ─────────── */}
      <UpcomingEventsSection />
      <AudienceCardsSection />
      <FeaturedCoursesSection featured={featured} />
      <TestimonialsSection />
      <PricingTeaserSection plans={plans} />

      <Footer />
    </div>
  );
}
